import IconCamera from '@/components/icon/IconCamera.vue'
import IconFilm from '@/components/icon/IconFilm.vue'
import IconImage from '@/components/icon/IconImage.vue'
import IconGlobe from '@/components/icon/IconGlobe.vue'
import IconMediaflowSingleTime from '@/components/icon/IconMediaflowSingleTime.vue'
import IconMediaflowRecurringTrigger from '@/components/icon/IconMediaflowRecurringTrigger.vue'
import IconMediaflowMultiAdBlock from '@/components/icon/IconMediaflowMultiAdBlock.vue'
import IconMediaflowSceneRotation from '@/components/icon/IconMediaflowSceneRotation.vue'
import IconMediaflowTimeSlotBlock from '@/components/icon/IconMediaflowTimeSlotBlock.vue'

/**
 * Presentation metadata shared by the Cloud Production (mediaflow) screens.
 * Pure helpers only — no API calls, no store access.
 */

export const MEDIA_TYPES = {
	live: { label: 'Livestream', short: 'Live', icon: IconCamera, viewBox: 16, fill: undefined, tone: 'text-main-light bg-main-transparent' },
	vod: { label: 'Video', short: 'VOD', icon: IconFilm, viewBox: 16, fill: undefined, tone: 'text-purple bg-surface-3' },
	image: { label: 'Image', short: 'Image', icon: IconImage, viewBox: 24, fill: 'none', tone: 'text-gold bg-surface-3' },
	url: { label: 'Web page', short: 'URL', icon: IconGlobe, viewBox: 16, fill: undefined, tone: 'text-success bg-surface-3' },
}

export const mediaTypeMeta = (type) => MEDIA_TYPES[type] || {
	label: type || 'Source', short: type || 'Source', icon: IconGlobe, viewBox: 16, fill: undefined, tone: 'text-surface-8 bg-surface-3',
}

/** True when a source carries audio the operator can mix (volume / mute / sync). */
export const mediaHasAudio = (media) => media?.type === 'live' || media?.type === 'vod'

/**
 * A flow's sources for the hover summary, top layer first, as Preview shows
 * them: `drafted` is the flow with its unapplied edits (or the flow itself).
 * Each item says whether it is hidden, muted and new / changed in the draft;
 * `draft` is true when the draft differs at all (removals and order included).
 */
export const flowSourceList = (flow, drafted = flow) => {
	const base = Array.isArray(flow?.media) ? flow.media : []
	const list = Array.isArray(drafted?.media) ? drafted.media : base
	const saved = new Map(base.map((m) => [m.id, m]))
	const items = list.map((m) => {
		const was = saved.get(m.id)
		let change = null
		if (!was) change = 'new'
		else if ((was.show !== false) !== (m.show !== false) || was.name !== m.name || was.type !== m.type || JSON.stringify(was.transform || null) !== JSON.stringify(m.transform || null)) change = 'changed'
		return { id: m.id, name: m.name || 'Untitled source', type: m.type, hidden: m.show === false, muted: mediaHasAudio(m) && !!m.muted, change }
	})
	const kept = list.filter((m) => saved.has(m.id)).map((m) => m.id)
	const savedOrder = base.filter((m) => kept.includes(m.id)).map((m) => m.id)
	const removed = base.filter((m) => !list.some((x) => x.id === m.id)).length
	const draft = items.some((i) => i.change) || removed > 0 || kept.join() !== savedOrder.join()
	return { items, removed, draft }
}

export const SCHEDULE_TYPES = {
	'single-time': { label: 'One-time', icon: IconMediaflowSingleTime, description: 'Switch once at a set time, then return.' },
	recurring: { label: 'Recurring', icon: IconMediaflowRecurringTrigger, description: 'Repeat on an interval inside a daily window.' },
	'multi-ad-block': { label: 'Ad block', icon: IconMediaflowMultiAdBlock, description: 'Play several scenes back to back.' },
	'scene-rotation': { label: 'Scene rotation', icon: IconMediaflowSceneRotation, description: 'Cycle through scenes on an interval.' },
	'time-slot-block': { label: 'Time slot', icon: IconMediaflowTimeSlotBlock, description: 'Repeat inside a time slot.' },
}

export const scheduleTypeMeta = (type) => SCHEDULE_TYPES[type] || { label: type || 'Schedule', icon: IconMediaflowSingleTime, description: '' }

/** "90" → "1m 30s", "3600" → "1h". */
export const formatSeconds = (value) => {
	const total = Math.max(0, parseInt(value, 10) || 0)
	if (total === 0) return '0s'
	const h = Math.floor(total / 3600)
	const m = Math.floor((total % 3600) / 60)
	const s = total % 60
	const parts = []
	if (h) parts.push(`${h}h`)
	if (m) parts.push(`${m}m`)
	if (s || parts.length === 0) parts.push(`${s}s`)
	return parts.join(' ')
}

/** Session countdown "mm:ss" or "h:mm:ss". */
export const formatCountdown = (sec) => {
	const total = Math.max(0, Math.floor(sec || 0))
	const h = Math.floor(total / 3600)
	const m = Math.floor((total % 3600) / 60)
	const s = total % 60
	const mm = m.toString().padStart(2, '0')
	const ss = s.toString().padStart(2, '0')
	return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

/** 4500 → "1h 15m", 90 → "1m 30s", 42 → "42s" (compact, for chips). */
export const formatDurationShort = (sec) => {
	const total = Math.max(0, Math.floor(sec || 0))
	const h = Math.floor(total / 3600)
	const m = Math.floor((total % 3600) / 60)
	const s = total % 60
	if (h > 0) return m ? `${h}h ${m}m` : `${h}h`
	if (m > 0) return s && m < 5 ? `${m}m ${s}s` : `${m}m`
	return `${s}s`
}

/** Local wall-clock "23:45" for a Date / timestamp. */
export const formatClockTime = (value) => {
	const date = value instanceof Date ? value : new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

/**
 * Session window from the reserved-time fields the API already returns
 * (`flowInstanceReservedAt` + `flowInstanceReserveTimeSec`). Pure: pass `now`.
 */
export const sessionWindow = (mediaflow, now = Date.now()) => {
	const reservedAt = mediaflow?.flowInstanceReservedAt ? new Date(mediaflow.flowInstanceReservedAt).getTime() : NaN
	const totalSec = Number(mediaflow?.flowInstanceReserveTimeSec) || 0
	if (!mediaflow?.obsInstance || Number.isNaN(reservedAt) || !totalSec) return null
	const endsAt = reservedAt + totalSec * 1000
	const remainingSec = Math.max(0, Math.floor((endsAt - now) / 1000))
	const elapsedSec = Math.max(0, Math.floor((now - reservedAt) / 1000))
	// Warn for the last 10 % of the window, clamped to 1–5 minutes, so a
	// 5-minute trial session and a 4-hour session both get a useful heads-up.
	const warnSec = Math.min(300, Math.max(60, Math.round(totalSec * 0.1)))
	return {
		startedAt: reservedAt,
		endsAt,
		totalSec,
		elapsedSec,
		remainingSec,
		endingSoon: remainingSec > 0 && remainingSec <= warnSec,
		progress: Math.max(0, Math.min(100, (elapsedSec / totalSec) * 100)),
	}
}

/**
 * What the server sees on an output, from the pulse payload the app already
 * polls (`alive`, `bitrate` kbps, `fps`, `width`, `height`).
 * `lastAliveAt` lets a brief dropout read as "Reconnecting" instead of "No signal".
 */
export const outputHealth = (pulse, { sessionActive = false, enabled = true, lastAliveAt = null, now = Date.now() } = {}) => {
	const details = []
	if (pulse?.alive) {
		if (pulse.bitrate) details.push(pulse.bitrate >= 1000 ? `${(pulse.bitrate / 1000).toFixed(1)} Mbps` : `${Math.round(pulse.bitrate)} kbps`)
		if (pulse.fps) details.push(`${Math.round(pulse.fps)} fps`)
		if (pulse.width && pulse.height) details.push(`${pulse.height}p`)
		return { key: 'healthy', label: 'Output healthy', tone: 'success', details }
	}
	if (!enabled) return { key: 'off', label: 'Production disabled', tone: 'muted', details }
	if (sessionActive && lastAliveAt && now - lastAliveAt < 90_000) {
		return { key: 'reconnecting', label: 'Reconnecting', tone: 'warning', details }
	}
	if (sessionActive) return { key: 'waiting', label: 'Waiting for signal', tone: 'warning', details }
	return { key: 'no-signal', label: 'No signal', tone: 'muted', details }
}

// ---------------------------------------------------------------- automation

const UNIT_MS = { second: 1000, minute: 60_000, hour: 3_600_000 }
const intervalMs = (interval) => {
	if (!interval) return 0
	if (typeof interval === 'string') {
		const m = interval.match(/(\d+)\s*(second|minute|hour)/i)
		return m ? Number(m[1]) * UNIT_MS[m[2].toLowerCase()] : 0
	}
	if (typeof interval === 'object' && interval.value !== undefined) {
		const unit = String(interval.unit || 'seconds').toLowerCase().replace(/s$/, '')
		return Number(interval.value) * (UNIT_MS[unit] || 1000)
	}
	return 0
}

/** Server clock time (UTC "HH:mm:ss" / { hours, minutes }) on a given UTC day → epoch ms. */
const clockOnDay = (value, dayMs) => {
	let h; let m; let s = 0
	if (typeof value === 'object' && value !== null && value.hours !== undefined) {
		h = value.hours; m = value.minutes || 0; s = value.seconds || 0
	} else if (typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
		[h, m, s = 0] = value.split(':').map(Number)
	} else if (typeof value === 'string') {
		const abs = absoluteTime(value)
		if (abs === null) return null
		const d = new Date(abs)
		h = d.getUTCHours(); m = d.getUTCMinutes(); s = d.getUTCSeconds()
	} else return null
	const day = new Date(dayMs)
	return Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), h, m, s)
}

/** Absolute server time (ISO, UTC when no zone given) → epoch ms, or null. */
const absoluteTime = (value) => {
	if (!value) return null
	if (value instanceof Date) return value.getTime()
	if (typeof value !== 'string') return null
	const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:?\d{2})?$/)
	if (m && !m[7]) return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0))
	const t = new Date(value).getTime()
	return Number.isNaN(t) ? null : t
}

/** Next fire inside a daily [start, end] window repeating every `step` ms, or null. */
const nextInDailyWindow = (startValue, endValue, step, now) => {
	for (let dayOffset = 0; dayOffset <= 1; dayOffset += 1) {
		const dayMs = now + dayOffset * 86_400_000
		const start = clockOnDay(startValue, dayMs)
		if (start === null) return null
		let end = endValue ? clockOnDay(endValue, dayMs) : start
		if (end !== null && end < start) end += 86_400_000
		if (now < start) return start
		if (step > 0 && end !== null && now <= end) {
			const next = start + Math.ceil((now - start) / step) * step
			if (next <= end) return next
		}
	}
	return null
}

/**
 * The next thing automation will do, from the ad-schedule list the app already
 * loads. `at` is epoch ms when it can be computed; otherwise `label` still
 * describes the cadence so the UI never claims a time it does not know.
 */
export const nextScheduledEvent = (schedules, flowName = () => '', now = Date.now()) => {
	const active = (Array.isArray(schedules) ? schedules : []).filter((s) => s && s.enabled)
	if (!active.length) return null
	const flow = (id) => flowName(id) || 'a scene'
	const candidates = active.map((schedule) => {
		let at = null
		let action = ''
		switch (schedule.type) {
			case 'single-time':
				at = absoluteTime(schedule.startTime)
				action = `Switch to ${flow(schedule.flowId)}`
				break
			case 'multi-ad-block': {
				at = absoluteTime(schedule.startTime)
				const ads = Array.isArray(schedule.ads) ? schedule.ads : []
				action = `Ad block (${ads.length} ${ads.length === 1 ? 'ad' : 'ads'})`
				break
			}
			case 'recurring':
				at = schedule.startTime ? nextInDailyWindow(schedule.startTime, schedule.endTime, intervalMs(schedule.repeatInterval), now) : null
				action = `Switch to ${flow(schedule.flowId)}`
				break
			case 'time-slot-block':
				at = schedule.startTime ? nextInDailyWindow(schedule.startTime, schedule.endTime, intervalMs(schedule.playInterval), now) : null
				action = 'Time slot'
				break
			case 'scene-rotation':
				at = null
				action = `Rotating ${formatInterval(schedule.rotationInterval) || 'scenes'}`
				break
			default:
				break
		}
		if (at !== null && at < now) at = null
		return { schedule, at, action, returnsTo: flowName(schedule.returnFlowId) || '' }
	})
	const timed = candidates.filter((c) => c.at !== null).sort((a, b) => a.at - b.at)
	const pick = timed[0] || candidates[0]
	if (!pick) return null
	const inSec = pick.at !== null ? Math.max(0, Math.round((pick.at - now) / 1000)) : null
	return {
		...pick,
		inSec,
		inLabel: inSec === null ? '' : inSec < 45 ? 'now' : `in ${formatDurationShort(inSec)}`,
		atLabel: pick.at !== null ? formatClockTime(pick.at) : '',
		activeCount: active.length,
	}
}

/** `{ value, unit }` or a preset string → "every 15 min". */
export const formatInterval = (interval) => {
	if (!interval) return ''
	if (typeof interval === 'string') return interval.replace(/^Every /i, 'every ')
	if (typeof interval === 'object' && interval.value !== undefined) {
		const value = Number(interval.value)
		const unit = String(interval.unit || 'seconds').toLowerCase().replace(/s$/, '')
		const short = { second: 's', minute: ' min', hour: ' h' }[unit] ?? ` ${unit}`
		return `every ${value}${short}`
	}
	return ''
}

const pad2 = (n) => String(n).padStart(2, '0')

/**
 * Server times arrive as UTC — either a full ISO string or a bare "HH:mm:ss"
 * clock time. Render both in the viewer's local time.
 */
export const formatScheduleTime = (value, { withDate = true } = {}) => {
	if (!value) return ''
	let date = null
	if (typeof value === 'object' && value !== null && value.hours !== undefined) {
		const now = new Date()
		date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), value.hours, value.minutes || 0, value.seconds || 0))
		withDate = false
	} else if (typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
		const [h, m, s = 0] = value.split(':').map(Number)
		const now = new Date()
		date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m, s))
		withDate = false
	} else if (typeof value === 'string') {
		const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:?\d{2})?$/)
		if (m && !m[7]) {
			date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)))
		} else {
			date = new Date(value)
		}
	} else if (value instanceof Date) {
		date = value
	}
	if (!date || Number.isNaN(date.getTime())) return String(value)
	const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
	if (!withDate) return time
	const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
	return `${day}, ${time}`
}

/**
 * One-line, human-readable description of when a schedule fires and what it
 * plays. `flowName(id)` resolves a flow id to its label.
 */
export const describeSchedule = (schedule, flowName = () => '') => {
	if (!schedule) return []
	const parts = []
	const flow = (id) => flowName(id) || 'a scene'
	switch (schedule.type) {
		case 'single-time':
			parts.push(formatScheduleTime(schedule.startTime))
			if (schedule.duration) parts.push(formatSeconds(schedule.duration))
			parts.push(`${flow(schedule.flowId)} → ${flow(schedule.returnFlowId)}`)
			break
		case 'recurring': {
			const interval = formatInterval(schedule.repeatInterval)
			if (interval) parts.push(interval)
			if (schedule.startTime || schedule.endTime) {
				parts.push(`${formatScheduleTime(schedule.startTime, { withDate: false })}–${formatScheduleTime(schedule.endTime, { withDate: false })}`)
			}
			if (schedule.duration) parts.push(formatSeconds(schedule.duration))
			parts.push(`${flow(schedule.flowId)} → ${flow(schedule.returnFlowId)}`)
			break
		}
		case 'multi-ad-block': {
			parts.push(formatScheduleTime(schedule.startTime))
			const ads = Array.isArray(schedule.ads) ? schedule.ads : []
			const total = ads.reduce((sum, ad) => sum + (parseInt(ad.duration, 10) || 0), 0)
			parts.push(`${ads.length} ${ads.length === 1 ? 'ad' : 'ads'}${total ? ` · ${formatSeconds(total)}` : ''}`)
			parts.push(`→ ${flow(schedule.returnFlowId)}`)
			break
		}
		case 'scene-rotation': {
			const interval = formatInterval(schedule.rotationInterval)
			if (interval) parts.push(interval)
			const scenes = Array.isArray(schedule.scenes) ? schedule.scenes : []
			parts.push(`${scenes.length} ${scenes.length === 1 ? 'scene' : 'scenes'}`)
			if (schedule.duration) parts.push(formatSeconds(schedule.duration))
			parts.push(`→ ${flow(schedule.returnFlowId)}`)
			break
		}
		case 'time-slot-block': {
			if (schedule.startTime || schedule.endTime) {
				parts.push(`${formatScheduleTime(schedule.startTime, { withDate: false })}–${formatScheduleTime(schedule.endTime, { withDate: false })}`)
			}
			const interval = formatInterval(schedule.playInterval)
			if (interval) parts.push(interval)
			if (schedule.duration) parts.push(formatSeconds(schedule.duration))
			parts.push(`→ ${flow(schedule.returnFlowId)}`)
			break
		}
		default:
			break
	}
	return parts.filter(Boolean)
}

/**
 * Layout presets for the transform editor. Positions are canvas pixels
 * (top-left of the source), scale is % of the source's natural size, which
 * for a canvas-sized source means % of the canvas.
 */
export const transformPresets = (canvasWidth = 1920, canvasHeight = 1080) => {
	const W = canvasWidth
	const H = canvasHeight
	const margin = Math.round(Math.min(W, H) * 0.025)
	const pip = 33
	const pipW = Math.round((W * pip) / 100)
	const pipH = Math.round((H * pip) / 100)
	return [
		{ key: 'full', label: 'Full screen', transform: { positionX: 0, positionY: 0, scaleX: 100, scaleY: 100 } },
		{ key: 'left-half', label: 'Left half', transform: { positionX: 0, positionY: 0, scaleX: 50, scaleY: 50 }, offsetY: Math.round((H - H / 2) / 2) },
		{ key: 'right-half', label: 'Right half', transform: { positionX: Math.round(W / 2), positionY: 0, scaleX: 50, scaleY: 50 }, offsetY: Math.round((H - H / 2) / 2) },
		{ key: 'top-left', label: 'Top left', transform: { positionX: margin, positionY: margin, scaleX: pip, scaleY: pip } },
		{ key: 'top-right', label: 'Top right', transform: { positionX: W - pipW - margin, positionY: margin, scaleX: pip, scaleY: pip } },
		{ key: 'bottom-left', label: 'Bottom left', transform: { positionX: margin, positionY: H - pipH - margin, scaleX: pip, scaleY: pip } },
		{ key: 'bottom-right', label: 'Bottom right', transform: { positionX: W - pipW - margin, positionY: H - pipH - margin, scaleX: pip, scaleY: pip } },
	].map((preset) => {
		if (preset.offsetY !== undefined) {
			const t = { ...preset.transform, positionY: preset.offsetY }
			return { key: preset.key, label: preset.label, transform: t }
		}
		return preset
	})
}

// ---------------------------------------------------------------- control room

/** The "+ Add ▾" toggle of a dock's add menu (DropDownMenu hands this straight to its button). */
export const DOCK_ADD_BUTTON = 'inline-flex h-7 items-center gap-1 rounded-md pl-1.5 pr-2 text-xs font-medium text-surface-text-1 ring-1 ring-surface-4 hover:bg-surface-3 hover:text-white hover:ring-surface-6'

/** Two-line item (label + hint) in a dock's "+" menu. */
export const DOCK_TEMPLATE_ITEM = 'flex h-auto min-h-11 w-full max-w-[90vw] items-center gap-3 px-3 py-2 text-left text-tiny leading-tight text-surface-text-1 hover:bg-surface-3 hover:text-white focus-visible:bg-surface-3 focus-visible:text-white focus-visible:outline-none disabled:opacity-50'

/** Destructive item in a dock's kebab menu. */
export const DOCK_DANGER_ITEM = 'h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 text-error-light hover:text-error-light text-nowrap leading-none text-base md:text-tiny flex items-center w-full truncate disabled:opacity-50'

/** Shown wherever graphics take / break cue need the new engine. */
export const ENGINE_HINT = 'Available on the new Cloud Production engine'

/** Scenes and breaks share the plan's scene limit (`maxMediaflowFlowNum`). */
export const scheduleLimitReached = (used, max) => max > 0 && used >= max
export const scheduleLimitReason = (used, max) =>
	`Schedule limit reached — ${used} of ${max} used. Delete a schedule or upgrade your plan.`

export const sceneLimitReached = (used, max) => max > 0 && used >= max
export const sceneLimitReason = (used, max) =>
	`Scene limit reached — ${used} of ${max} used (breaks count as scenes). Delete a scene or break, or upgrade your plan.`

/** MIME type used when an input tile is dragged onto the Preview canvas. */
export const INPUT_DRAG_TYPE = 'application/x-castr-input'

/** Flows that are layouts (what an operator previews and sends to live). */
export const layoutFlows = (flows) => (Array.isArray(flows) ? flows.filter((f) => f && f.kind !== 'break') : [])

/** Flows that are breaks (ad pods, L-bands, full-screen ads) — cued, auto-return. */
export const breakFlows = (flows) => (Array.isArray(flows) ? flows.filter((f) => f && f.kind === 'break') : [])

/** Overlays grouped by `row`, each group ordered by `order`. Rows keep first-seen order. */
export const overlayRows = (overlays) => {
	const rows = new Map()
	;(Array.isArray(overlays) ? overlays : []).forEach((overlay) => {
		if (!overlay) return
		const row = overlay.row || 'Graphics'
		if (!rows.has(row)) rows.set(row, [])
		rows.get(row).push(overlay)
	})
	return Array.from(rows.entries()).map(([name, items]) => ({
		name,
		items: [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
	}))
}

/** Stable identity of a media item across flows (same stream / file / image / page). */
export const mediaIdentity = (media) => {
	if (!media) return ''
	switch (media.type) {
		case 'live': {
			const s = media.stream
			return `live:${typeof s === 'object' && s !== null ? (s._id || s.id) : s}`
		}
		case 'vod': return `vod:${media.file || media.stream || media.downloadUrl || media.name}`
		case 'image': return `image:${media.imageId || media.imageUrl || media.name}`
		case 'url': return `url:${media.sourceUrl || media.name}`
		default: return `${media.type}:${media.name}`
	}
}

/**
 * The production's inputs: every distinct source used by any flow, with the
 * flows it appears in. Mirrors the "inputs" row of a hardware switcher.
 */
export const productionInputs = (flows) => {
	const seen = new Map()
	;(Array.isArray(flows) ? flows : []).forEach((flow) => {
		;(Array.isArray(flow?.media) ? flow.media : []).forEach((media) => {
			const key = mediaIdentity(media)
			if (!key) return
			if (!seen.has(key)) seen.set(key, { key, media, flows: [] })
			seen.get(key).flows.push(flow)
		})
	})
	return Array.from(seen.values())
}

/** Fields to copy when the same source is added to another flow (ids are per flow). */
export const cloneMediaPayload = (media, overrides = {}) => {
	if (!media) return null
	const payload = { name: media.name, type: media.type }
	if (media.type === 'live') payload.stream = typeof media.stream === 'object' && media.stream !== null ? (media.stream._id || media.stream.id) : media.stream
	if (media.type === 'vod') {
		['stream', 'key', 'file', 'fileName', 'fileNmae', 'loop', 'restartPlayback', 'showEmptyOnEnd', 'downloadUrl', 'refId'].forEach((k) => {
			if (media[k] !== undefined) payload[k] = media[k]
		})
	}
	if (media.type === 'image') { payload.imageId = media.imageId; payload.imageUrl = media.imageUrl }
	if (media.type === 'url') payload.sourceUrl = media.sourceUrl
	return { ...payload, ...overrides }
}

/** Every source name used in the production (scenes, breaks and graphics). */
export const productionMediaNames = (flows, overlays = []) => {
	const names = new Set()
	;(Array.isArray(flows) ? flows : []).forEach((flow) => {
		;(Array.isArray(flow?.media) ? flow.media : []).forEach((m) => { if (m?.name) names.add(m.name) })
	})
	;(Array.isArray(overlays) ? overlays : []).forEach((o) => {
		if (o?.name) names.add(o.name)
		if (o?.media?.name) names.add(o.media.name)
	})
	return names
}

/**
 * A source name not in `taken`, which it is added to. The server rejects a
 * name already used anywhere in the production, so a source copied into
 * another scene gets that scene's name in brackets, then a number.
 */
export const uniqueMediaName = (base, taken, flowName = '') => {
	const name = String(base || 'Source').trim()
	const candidates = [name]
	if (flowName) candidates.push(`${name} (${flowName})`)
	let picked = candidates.find((c) => !taken.has(c))
	for (let n = 2; !picked; n += 1) {
		const c = flowName ? `${name} (${flowName} ${n})` : `${name} ${n}`
		if (!taken.has(c)) picked = c
	}
	taken.add(picked)
	return picked
}

/**
 * Quick graphics templates: where a new overlay lands on the canvas. Sizes are
 * % of the canvas, positions canvas px (the server's transform shape). The
 * first five are offered in the Graphics dock's "+" menu.
 */
export const graphicTemplates = (canvas = { width: 1920, height: 1080 }) => {
	const W = canvas.width
	const H = canvas.height
	const m = Math.round(Math.min(W, H) * 0.025)
	const r2 = (n) => Math.round(n * 100) / 100
	const t = (x, y, sx, sy) => ({ positionX: Math.round(x), positionY: Math.round(y), scaleX: r2(sx), scaleY: r2(sy) })
	return [
		{ key: 'logo-bug', label: 'Logo bug', hint: 'Small, top-right corner', row: 'Graphics', types: ['image'], transform: t(W - W * 0.12 - m, m, 12, 12) },
		{ key: 'lower-third', label: 'Lower third', hint: 'Name / title band', row: 'Graphics', types: ['image', 'url'], transform: t(0, H * (2 / 3), 100, 100 / 3) },
		{ key: 'ticker', label: 'Ticker', hint: 'Thin strip along the bottom', row: 'Graphics', types: ['url', 'image'], transform: t(0, H - H * 0.08, 100, 8) },
		{ key: 'scoreboard', label: 'Scoreboard / web graphic', hint: 'Live HTML page, top-left', row: 'Graphics', types: ['url', 'image'], transform: t(m, m, 28, 10) },
		{ key: 'pip-video', label: 'PiP video', hint: 'Clip in the bottom-right corner', row: 'Graphics', types: ['vod', 'url'], transform: t(W - W * 0.3 - m, H - H * 0.3 - m, 30, 30) },
		{ key: 'l-band', label: 'L-band frame', hint: 'Full-frame image with a cut-out', row: 'Graphics', types: ['image'], transform: t(0, 0, 100, 100) },
		{ key: 'full', label: 'Full frame', hint: 'Covers the whole canvas', row: 'Graphics', types: ['image', 'url', 'vod'], transform: t(0, 0, 100, 100) },
	]
}

/**
 * Starting points for a new scene. `slots` are the sources the operator picks
 * (from inputs already used in the production), listed top layer first, each
 * with where it lands. Blank needs none.
 */
export const sceneTemplates = (canvas = { width: 1920, height: 1080 }) => {
	const W = canvas.width
	const H = canvas.height
	const portrait = H > W
	const m = Math.round(Math.min(W, H) * 0.025)
	const pip = portrait ? 40 : 30
	const r2 = (n) => Math.round(n * 100) / 100
	const t = (x, y, sx, sy) => ({ positionX: Math.round(x), positionY: Math.round(y), scaleX: r2(sx), scaleY: r2(sy) })
	const full = t(0, 0, 100, 100)
	return [
		{ key: 'blank', label: 'Blank', hint: 'Add sources yourself', slots: [] },
		{ key: 'single', label: 'Single source', hint: 'One source, full screen', slots: [{ key: 'main', label: 'Source', transform: full }] },
		{
			key: 'pip',
			label: 'Picture-in-picture',
			hint: 'Main source + small corner box',
			slots: [
				{ key: 'second', label: 'Corner source', transform: t(W - (W * pip) / 100 - m, H - (H * pip) / 100 - m, pip, pip) },
				{ key: 'main', label: 'Main source', transform: full },
			],
		},
		{
			key: 'side',
			label: 'Side by side',
			hint: 'Two sources, half each',
			slots: [
				{ key: 'main', label: 'Left source', transform: t(0, H * 0.25, 50, 50) },
				{ key: 'second', label: 'Right source', transform: t(W / 2, H * 0.25, 50, 50) },
			],
		},
		{ key: 'l-band', label: 'L-band', hint: 'Source squeezed top-right', slots: [{ key: 'main', label: 'Source', transform: t(W * 0.25, 0, 75, 75) }] },
	]
}

/** The program input squeezed for an L-band: top-right 75 %. */
export const lBandProgramTransform = (canvas = { width: 1920, height: 1080 }) => ({
	positionX: Math.round(canvas.width * 0.25), positionY: 0, scaleX: 75, scaleY: 75,
})

/** Remaining seconds of a running cue (null when none / no deadline). */
export const cueRemainingSec = (cue, now = Date.now()) => {
	if (!cue?.returnsAt) return null
	const at = new Date(cue.returnsAt).getTime()
	if (Number.isNaN(at)) return null
	return Math.max(0, Math.ceil((at - now) / 1000))
}

/**
 * Timed events automation will fire in the next `horizonMs` (default 2 h),
 * for the rundown strip. Recurring schedules expand to every fire time in the
 * window (capped), untimed ones (flow rotation) are left out.
 */
export const upcomingScheduledEvents = (schedules, flowName = () => '', now = Date.now(), horizonMs = 2 * 3_600_000, cap = 24) => {
	const active = (Array.isArray(schedules) ? schedules : []).filter((s) => s && s.enabled)
	const out = []
	const flow = (id) => flowName(id) || 'a scene'
	const push = (schedule, at, action, durationSec) => {
		if (at === null || at < now - 1000 || at > now + horizonMs) return
		out.push({
			id: `${schedule._id}-${at}`,
			schedule,
			at,
			action,
			durationSec: durationSec || 0,
			returnsTo: flowName(schedule.returnFlowId) || '',
			atLabel: formatClockTime(at),
			kind: schedule.type === 'multi-ad-block' ? 'ad' : 'switch',
		})
	}
	active.forEach((schedule) => {
		switch (schedule.type) {
			case 'single-time':
				push(schedule, absoluteTime(schedule.startTime), `Switch to ${flow(schedule.flowId)}`, parseInt(schedule.duration, 10))
				break
			case 'multi-ad-block': {
				const ads = Array.isArray(schedule.ads) ? schedule.ads : []
				const total = ads.reduce((sum, ad) => sum + (parseInt(ad.duration, 10) || 0), 0)
				push(schedule, absoluteTime(schedule.startTime), `Ad block · ${ads.length} ${ads.length === 1 ? 'ad' : 'ads'}`, total)
				break
			}
			case 'recurring':
			case 'time-slot-block': {
				const step = intervalMs(schedule.type === 'recurring' ? schedule.repeatInterval : schedule.playInterval)
				if (!schedule.startTime) break
				let at = nextInDailyWindow(schedule.startTime, schedule.endTime, step, now)
				let guard = 0
				while (at !== null && at <= now + horizonMs && guard < cap) {
					push(schedule, at, schedule.type === 'recurring' ? `Switch to ${flow(schedule.flowId)}` : 'Time slot', parseInt(schedule.duration, 10))
					guard += 1
					if (!step) break
					at = nextInDailyWindow(schedule.startTime, schedule.endTime, step, at + 1000)
				}
				break
			}
			default:
				break
		}
	})
	return out.sort((a, b) => a.at - b.at).slice(0, cap)
}

/**
 * Server errors about scenes and breaks in the words the UI uses ("flow" is
 * the API's name for both; they share one set of names).
 */
export const flowErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
	const message = String(err?.message || err || '')
	if (/flow with this name already exists/i.test(message)) return 'A scene or break with this name already exists. Pick another name.'
	return message || fallback
}

/** The draft's apply button: Program only reaches viewers while a session runs. */
export const draftApplyLabel = (sessionActive) => (sessionActive ? 'Apply to live' : 'Apply to Program')

/**
 * A web page source's address: `https://` is added when no scheme is typed;
 * anything that is not an http(s) address with a real host name is refused.
 * Returns `{ url }` or `{ error }` (empty input is neither).
 */
export const normalizeWebPageUrl = (input) => {
	const raw = String(input ?? '').trim()
	if (!raw) return { url: '', error: '' }
	const invalid = { url: '', error: 'Enter a full web address, e.g. https://example.com/scoreboard' }
	if (/\s/.test(raw)) return invalid
	const withScheme = /^[a-z][a-z\d+.-]*:/i.test(raw) ? raw : `https://${raw.replace(/^\/\//, '')}`
	let parsed
	try { parsed = new URL(withScheme) } catch (e) { return invalid }
	if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { url: '', error: 'Use an http:// or https:// address.' }
	const host = parsed.hostname
	const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[')
	if (!isIp && host !== 'localhost' && !/^([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}$/i.test(host)) return invalid
	return { url: parsed.href, error: '' }
}

/** Second line of a source row: the livestream's name, the clip's file or the page's host. */
export const mediaSubtitle = (media, streams = []) => {
	if (!media) return ''
	if (media.type === 'live') {
		const id = typeof media.stream === 'object' && media.stream !== null ? (media.stream._id || media.stream.id) : media.stream
		return streams.find((s) => s._id === id)?.name || ''
	}
	if (media.type === 'vod') return media.fileName || media.file || ''
	if (media.type === 'url') {
		try { return new URL(media.sourceUrl).host } catch { return media.sourceUrl || '' }
	}
	return ''
}

/** "1920 x 1080" → { width: 1920, height: 1080 } (defaults to landscape 1080p). */
export const parseScreenResolution = (screenResolution, preset) => {
	const m = String(screenResolution || '').match(/(\d+)\s*x\s*(\d+)/i)
	if (m) return { width: parseInt(m[1], 10), height: parseInt(m[2], 10) }
	if (preset === '720p') return { width: 1280, height: 720 }
	return { width: 1920, height: 1080 }
}
